import { useEffect, useState } from "react";
import axios from "axios";
import "./beta.css";
import { BetaJourney } from "./beta-journey";
const client = axios.create({ withCredentials: true });
const base = "/api/secretmissions/editor/";
type Tester = {
  memberId: number;
  username: string;
  name?: string;
  phone: string;
  completed: number;
  missionNumber: number;
  complete: boolean;
  mission: { name: string } | null;
  step: string;
  stepId: number | null;
  handler: string;
  firstOpenedAt: string | null;
  startedAt: string | null;
  lastActivityAt: string | null;
  entryLink: string;
  handlerPhase: string | null;
};
const message = (e: unknown) =>
  axios.isAxiosError(e)
    ? e.response?.data?.message || "Please try again."
    : "Please try again.";
function DateLabel({ value }: { value: string | null }) {
  return value ? (
    <time dateTime={value}>
      {new Date(value).toLocaleString("en-US", {
        timeZone: "America/Chicago",
        timeZoneName: "short",
      })}
    </time>
  ) : (
    <>—</>
  );
}
export function BetaAdmin() {
  const [auth, setAuth] = useState(false),
    [csrf, setCsrf] = useState(""),
    [loading, setLoading] = useState(true),
    [phone, setPhone] = useState(""),
    [code, setCode] = useState(""),
    [sent, setSent] = useState(false),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [rows, setRows] = useState<Tester[]>([]),
    [updated, setUpdated] = useState(""),
    [copied, setCopied] = useState<number | null>(null);
  useEffect(() => {
    let live = true;
    client
      .get(base + "session/")
      .then(({ data }) => {
        if (live) {
          setAuth(data.result.authenticated);
          setCsrf(data.result.csrfToken);
        }
      })
      .catch((e) => {
        if (live) setError(message(e));
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, []);
  useEffect(() => {
    if (!auth) return;
    let live = true;
    const load = async () => {
      try {
        const { data } = await client.get(base + "beta/");
        if (live) {
          setRows(data.result.testers);
          setUpdated(data.result.updatedAt);
          setError("");
        }
      } catch (e) {
        if (!live) return;
        setError(message(e));
        if (
          axios.isAxiosError(e) &&
          [401, 403].includes(e.response?.status ?? 0)
        ) {
          setRows([]);
          setAuth(false);
        }
      }
    };
    void load();
    const timer = setInterval(() => void load(), 15000);
    return () => {
      live = false;
      clearInterval(timer);
    };
  }, [auth]);
  const headers = { "X-CSRFToken": csrf };
  const login = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (!sent) {
        await client.post(base + "phone-code/", { phone }, { headers });
        setSent(true);
      } else {
        await client.post(base + "session/", { code }, { headers });
        const { data } = await client.get(base + "session/");
        setCsrf(data.result.csrfToken);
        setAuth(data.result.authenticated);
      }
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  };
  const logout = async () => {
    setBusy(true);
    try {
      await client.delete(base + "session/", { headers });
      setRows([]);
      setAuth(false);
      setSent(false);
      setCode("");
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <main className="beta-page">
      <header>
        <div>
          <span className="beta-kicker">Secret Missions · Admin</span>
          <h1>Beta progress</h1>
        </div>
        {auth && (
          <button onClick={() => void logout()} disabled={busy}>
            Sign out
          </button>
        )}
      </header>
      <p>
        Track each tester through their seven missions. Times shown in Central
        Time (CST/CDT).
      </p>
      {error && (
        <p role="alert" className="beta-error">
          {error}
        </p>
      )}
      {loading ? (
        <p role="status">Checking admin session…</p>
      ) : !auth ? (
        <form onSubmit={login}>
          <h2>Admin sign-in</h2>
          <p>Use an authorized Mission Editor phone number.</p>
          <label>
            {sent ? "Verification code" : "Phone number"}
            <input
              type={sent ? "text" : "tel"}
              inputMode={sent ? "numeric" : "tel"}
              autoComplete={sent ? "one-time-code" : "tel"}
              value={sent ? code : phone}
              onChange={(e) =>
                sent ? setCode(e.target.value) : setPhone(e.target.value)
              }
              required
              maxLength={sent ? 6 : 24}
            />
          </label>
          <div className="beta-actions">
            <button disabled={busy || !csrf}>
              {busy ? "Please wait…" : sent ? "Sign in" : "Send code"}
            </button>
            {sent && (
              <button
                type="button"
                onClick={() => {
                  setSent(false);
                  setCode("");
                }}
                disabled={busy}
              >
                Change number
              </button>
            )}
          </div>
        </form>
      ) : (
        <>
          <p>
            Updates every 15 seconds. Last refreshed:{" "}
            <DateLabel value={updated || null} />
          </p>
          <p>
            Entry links grant access to that tester’s account and expire after
            30 days. Share each link only with its assigned tester.
          </p>
          <div className="beta-grid">
            {rows.map((row) => (
              <section className="beta-card" key={row.memberId}>
                <h2>{row.username}</h2>
                <p>
                  Member {row.memberId} ·{" "}
                  {row.complete
                    ? "All seven completed"
                    : `${row.completed} of 7 completed`}
                </p>
                <progress max={7} value={row.completed} />
                <dl>
                  <dt>Name</dt>
                  <dd>{row.name || "Not provided yet"}</dd>
                  <dt>Mission</dt>
                  <dd>
                    {row.complete ? "Complete" : `${row.missionNumber} of 7`}
                    {row.mission && <> · {row.mission.name}</>}
                  </dd>
                  <dt>Current step</dt>
                  <dd>
                    {row.step}
                    {row.stepId ? ` (#${row.stepId})` : ""}
                  </dd>
                  <dt>Handler</dt>
                  <dd>
                    {row.handler || "Not chosen"}
                    {row.handlerPhase ? ` · ${row.handlerPhase}` : ""}
                  </dd>
                  <dt>Phone</dt>
                  <dd>{row.phone || "Not assigned"}</dd>
                  <dt>First opened</dt>
                  <dd>
                    <DateLabel value={row.firstOpenedAt} />
                  </dd>
                  <dt>Flow started</dt>
                  <dd>
                    <DateLabel value={row.startedAt} />
                  </dd>
                  <dt>Last activity</dt>
                  <dd>
                    <DateLabel value={row.lastActivityAt} />
                  </dd>
                </dl>
                <button
                  onClick={() => {
                    void navigator.clipboard
                      .writeText(row.entryLink.replace(/^https:\/\//i, ""))
                      .then(() => setCopied(row.memberId))
                      .catch(() =>
                        setError(
                          "Could not copy the link. Check clipboard permission.",
                        ),
                      );
                  }}
                >
                  {copied === row.memberId
                    ? "Copied entry link"
                    : "Copy entry link"}
                </button>
                <BetaJourney
                  memberId={row.memberId}
                  name={row.name || row.username}
                />
              </section>
            ))}
          </div>
        </>
      )}
    </main>
  );
}
